import Keycloak, { KeycloakConfig, KeycloakInitOptions } from 'keycloak-js';

import { getConfigs, isObjectEmpty, getOIDCEndPoints, createUUID, getQueryString } from './helper';
import {
  CURRENT_ENV,
  CURRENT_ENV_NAMES,
  GRANT_TYPE,
  GRANT_TYPE_NAMES,
  LOGIN_INITIATE_TYPES,
  GLOBAL_LISTENER_EVENTS,
  LOGIN_INITIATE_TYPES_NAMES,
} from './types';

export class IMAuthSDK {
  keycloakObj: Keycloak;

  private _domainName: string;
  private _keyCloakOIDCEndPoints: { [key: string]: any } = {};
  private _keyCloakConfig: KeycloakConfig;
  private _allowConsoleLog = false;

  /**
   * _auth0Configs param is deprecated and not used by this SDK as auth0-js package has been removed. You need to pass null or {} in _auth0Configs param.
   */
  constructor(keyCloackConfigs: KeycloakConfig, _auth0Configs: any, currentEnv: CURRENT_ENV = CURRENT_ENV_NAMES.PROD) {
    this._keyCloakConfig = keyCloackConfigs;

    this.keycloakObj = new Keycloak({
      ...keyCloackConfigs,
    });

    const configsObj = getConfigs(currentEnv);
    this._domainName = configsObj.domain;
    this._allowConsoleLog = currentEnv === CURRENT_ENV_NAMES.PROD ? false : true;

    this.checkSession = this.checkSession.bind(this);
    this._init = this._init.bind(this);
    this._checkInitNotCompleted = this._checkInitNotCompleted.bind(this);
    this.loginToggleModal = this.loginToggleModal.bind(this);
    this.auth0LogoutMS1 = this.auth0LogoutMS1.bind(this);
    this.silentLogin = this.silentLogin.bind(this);

    this._init();
  }

  private async _init(): Promise<void> {
    try {
      if (isObjectEmpty(this._keyCloakOIDCEndPoints)) {
        this._keyCloakOIDCEndPoints = await getOIDCEndPoints(
          `${this._keyCloakConfig.url}/realms/${this._keyCloakConfig.realm}/.well-known/openid-configuration`
        );
      }
    } catch (error) {
      if (this._allowConsoleLog) {
        console.log('IMAuthSDK Error from _init : ', error);
      }
    }
  }

  private async _checkInitNotCompleted(): Promise<boolean> {
    return isObjectEmpty(this._keyCloakOIDCEndPoints);
  }

  /**
   * The checkSession method allows you to acquire a new token from Auth Env for a user who is already authenticated against Auth Env for your domain.
   *
   * If user is not authenticated or checkSession call gets failed due to any reason will result in throwing of error with object describing the cause of the error.
   *
   * However, the browser must have third-party cookies enabled. Otherwise, checkSession() is unable to access the current user's session.
   *
   * _auth0CheckSessionOptions param is deprecated and not used by this SDK as auth0-js package has been removed. You need to pass null or {} in _auth0Configs param.
   */
  public async checkSession(
    keyCloakInitoptions: KeycloakInitOptions,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _auth0CheckSessionOptions: any
  ): Promise<unknown> {
    if (await this._checkInitNotCompleted()) {
      await this._init();
    }

    return new Promise((resolve, reject) => {
      const keycloakObjTemp = this.keycloakObj;
      keycloakObjTemp
        .init({
          onLoad: 'check-sso',
          silentCheckSsoRedirectUri: window.location.origin + '/im-sso/silent-check-sso.html',
          responseMode: 'query',
          pkceMethod: 'S256',
          silentCheckSsoFallback: false,
          ...keyCloakInitoptions,
        })
        .then(() => {
          if (keycloakObjTemp && keycloakObjTemp.authenticated) {
            const keycloakResponseObj = {
              accessToken: keycloakObjTemp.token || '',
              idToken: keycloakObjTemp.idToken || '',
              idTokenPayload: keycloakObjTemp.idTokenParsed ?? null,
              refreshToken: keycloakObjTemp.refreshToken || '',
              tokenType: keycloakObjTemp.tokenParsed?.typ || '',
              appState: keycloakObjTemp.tokenParsed?.session_state || '',
              state: keycloakObjTemp.tokenParsed?.session_state || '',
              scope: keycloakObjTemp.tokenParsed?.scope || '',
              expiresIn: (keycloakObjTemp.tokenParsed?.exp || 0) - (keycloakObjTemp.tokenParsed?.iat || 0),
            };
            resolve(keycloakResponseObj);
          } else {
            reject({
              error: 'login_required',
              error_description: 'Login required',
            });
          }
        })
        .catch(err => {
          let errorObj = err;
          if (typeof err === 'string') {
            errorObj = {
              error: err,
              error_description: err,
            };
          }
          reject(errorObj);
        });
    });
  }

  /**
   * The oauthtoken method allows you to get new token based on the grant type (authorization_code | refresh_token) and value provided.
   */
  public async oauthToken(grantType: GRANT_TYPE, value: string, redirectURI: string): Promise<unknown> {
    if (await this._checkInitNotCompleted()) {
      await this._init();
    }

    return new Promise((resolve, reject) => {
      if (this.keycloakObj.flow !== 'implicit' && value) {
        const queryParams = {
          grant_type: grantType,
          client_id: encodeURIComponent(this._keyCloakConfig.clientId),
          redirect_uri: redirectURI,
        };

        if (grantType === GRANT_TYPE_NAMES.AUTHORIZATION_CODE) {
          queryParams['code'] = value;
        } else {
          queryParams['refresh_token'] = value;
        }

        let url = this._keyCloakOIDCEndPoints?.token_endpoint;

        /** if somehow OIDC end point is undefined then we will create URL using keyCloackConfig */
        if (!url) {
          url = `${this._keyCloakConfig.url}/realms/${this._keyCloakConfig.realm}/protocol/openid-connect/token`;
        }

        const req = new XMLHttpRequest();
        req.open('POST', url, true);
        req.setRequestHeader('Content-type', 'application/x-www-form-urlencoded');

        req.withCredentials = true;

        req.onreadystatechange = function () {
          if (req.readyState === 4) {
            if (req.status === 200) {
              const tokenResponse = JSON.parse(req.responseText);
              resolve(tokenResponse);
            } else {
              reject(req.responseText);
            }
          }
        };

        req.send(getQueryString(queryParams));
      } else {
        reject('Either code is blank or CODE exchange not allowed in implicit mode');
      }
    });
  }

  /**
   * The loginToggleModal method will redirect you to sso univeral page.
   *
   * If you want to pass only redirectUrl param, then you can bypass type param with value none.
   */
  public loginToggleModal(type?: LOGIN_INITIATE_TYPES, redirectUrl?: string): void {
    const header = document.getElementsByTagName('header');
    if (header && header.length > 0) {
      window.postMessage(
        JSON.stringify({
          eventType: GLOBAL_LISTENER_EVENTS.GLOBAL_LOGIN,
          redirectUrl: redirectUrl || '',
          type: type || LOGIN_INITIATE_TYPES_NAMES.NONE,
        }),
        '*'
      );
    } else {
      localStorage.setItem('keycloak-sdk-login-redirect', type ?? '');
      window.location.href = this._domainName + '/login?returnUrl=' + escape(redirectUrl ?? window.location.href);
    }
  }

  /**
   * The auth0LogoutMS1 method allows you to logged out from the system.
   */

  public auth0LogoutMS1(redirectUrl?: string): void {
    window.postMessage({ eventType: GLOBAL_LISTENER_EVENTS.GLOBAL_LOGOUT, redirectUrl: redirectUrl || '' }, '*');
  }

  /** 
   * The silentLogin method allows you to login into the system with username and password.
   * 
   * Please use this method only if you want to bypass sso universal login 
   * 
   * You need to execute below codes on page load of your redirectURI
   * 
   * ex:
   * ```js
      let loginIframe = window?.parent?.document?.querySelector('iframe#login-frame');
   
      if (loginIframe) {
         window.parent.location.href = window.location.href;
         loginIframe.remove();
      }
  * ```
  **/
  public silentLogin(
    options: {
      username: string;
      password: string;
      scope: string;
      redirectURI: string;
      env: string;
      prompt: 'consent' | 'none';
      state?: string;
      nonce?: string;
      auth0_realm: string;
      RM: 'enrol' | 'none';
    },
    errorCallback: (error: any) => void
  ): void {
    const { username, password, redirectURI, scope, RM } = options;

    const nonce = options.nonce ? options.nonce : createUUID();

    this._checkInitNotCompleted().then(async (notCompleted: boolean) => {
      if (notCompleted) {
        await this._init();
      }

      const iFrameRedirectURL = encodeURI(`${redirectURI}${encodeURIComponent('&' + options.state)}`);
      const keyCloakOrigin = new URL(this._keyCloakConfig.url as string).origin;

      let iFrameSrc = this._keyCloakOIDCEndPoints?.authorization_endpoint;

      const queryParams = {
        client_id: this._keyCloakConfig.clientId,
        redirect_uri: iFrameRedirectURL,
        response_mode: 'query',
        response_type: 'code',
        scope,
        nonce,
        RM,
      };

      /** if somehow OIDC end point is undefined then we will create URL using keyCloackConfig */
      if (!iFrameSrc) {
        iFrameSrc = `${this._keyCloakConfig.url}/realms/${this._keyCloakConfig.realm}/protocol/openid-connect/auth`;
      }

      iFrameSrc += `?${getQueryString(queryParams)}`;

      const loginFrame = document.createElement('iframe');
      loginFrame.style.display = 'none';
      loginFrame.setAttribute('id', 'login-frame');
      loginFrame.onload = () => {
        const iframe = document.querySelector('iframe#login-frame') as HTMLIFrameElement;
        if (this._allowConsoleLog) {
          console.log('on iframe load : ', iframe?.src);
        }

        const data = {
          eventType: 'LOGIN_REQUEST',
          username,
          password,
        };

        iframe?.contentWindow?.postMessage(JSON.stringify(data), keyCloakOrigin);

        window.addEventListener(
          'message',
          event => {
            if (this._allowConsoleLog) {
              console.log('event triggered in loginFrame --> ', event, event?.data);
            }

            try {
              if (event && event.data) {
                const eventData = JSON.parse(event.data);
                if (eventData.eventType === 'LOGIN_ERROR') {
                  if (this._allowConsoleLog) {
                    console.log('LOGIN_ERROR event fired : ', eventData);
                    console.log('removing iframe from body');
                  }
                  loginFrame?.remove();
                  errorCallback(eventData?.error);
                }
              }
              // eslint-disable-next-line no-empty
            } catch (e) {}
          },
          false
        );
      };

      loginFrame.src = iFrameSrc;
      document.body.appendChild(loginFrame);
    });
  }
}
